import React, { useCallback, useEffect, useMemo, useState } from "react";
import i18n from "i18next";
import { useDispatch } from "react-redux";
import { applyInteractiveResult } from "./applyInteractiveResult";
import { composeNarration, draftTextOf } from "./composeNarration";
import {
  DEFAULT_INTERACTIVE_COLOR,
  INTERACTIVE_COLORS,
  INTERACTIVE_MODELS,
  fetchInteractiveCatalog,
  generateInteractive,
} from "./interactiveItemService";
import InteractiveExample from "./InteractiveExample";
import InteractiveCreditButton from "./InteractiveCreditButton";
import { useHtmlByUrl } from "../../../common/html_artifact_store.es6";
import "./_interactiveItem.scss";

/* eslint-disable react/prop-types */

/**
 * "İnteraktif" sekmesi — Soru Üretici modalının üçüncü sekmesi.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * NEDEN MODAL DEĞİL, PANEL
 * ──────────────────────────────────────────────────────────────────────────
 * Bu akış bir süre Bileşenler → Etkileşim panelinde kendi overlay modalı
 * olarak durdu ve orada BOZUK çıktı: carousel paneli `transform` taşıyor,
 * transform'lu bir ata ise `position: fixed`'in referansını kendisi yapıyor.
 * Overlay ekranı değil o dar paneli kapladı — 124 piksellik bir şeride
 * sıkışmış, her kelimesi alt alta düşen bir modal.
 *
 * İkinci ve asıl sebep: yer yanlıştı. Soru Üretici zaten "anlatımdan AI ile
 * üret" merkezi; sahne listesi, anlatım metni ve anlatıcı ayarları orada
 * hazır duruyor. Burası o modalın içinde yaşayan bir PANEL — kendi overlay'i,
 * kendi sahne listesi, kendi anlatım kutusu yok. Host ne gösteriyorsa onu
 * kullanır.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * ÜÇ ADIM
 * ──────────────────────────────────────────────────────────────────────────
 * Katalogda 23 aktivite var. Hepsini bir ızgarada göstermek seçim felci
 * yapıyor; "tek düğme, model karar versin" ise kullanıcıyı ne geleceğini
 * bilmeden ücretli bir üretime sokuyor.
 *
 *   1. AMAÇ  — altı kutu. 23 tip bunların altında.
 *   2. ÖNERİ — model anlatımı okur, üç somut öneri çıkarır. Ucuz, senkron,
 *              ücretsiz. İsteyen "Tüm tipleri göster" ile listeye iner.
 *   3. ONAY  — üretilen aktivite OYNANABİLİR gösterilir; beğenilirse sahneye
 *              eklenir.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * PUANSIZ TİPTE EGZERSİZ KAYDI AÇILMAZ
 * ──────────────────────────────────────────────────────────────────────────
 * `exercises{}` kaydını ADD_BOX reducer'ı açar ve açma koşulu `ids.config`
 * içindeki kategorinin "evaluation" olması. Bu yüzden config SADECE backend
 * bir `exercise` döndürdüğünde — yani tip puanlıysa — geçilir.
 *
 * Çevrilen kartların ya da zaman çizelgesinin egzersiz kaydı açılsaydı,
 * ağırlığı sıfır olsa bile eğitimin "kaç soru doğru" paydasında görünür ve
 * öğrenci hiç cevaplamadığı bir şeyden eksik alırdı.
 */
export default function InteractivePane({
  /**
   * Aktivitenin düşeceği sahne — host'un çözdüğü "anchor": sol listede seçili
   * sahnelerin sonuncusu. Burada hesaplanmıyor çünkü seçim host'un
   * (sol listenin) elinde.
   */
  navId,
  sceneTitle,
  sceneIndex,
  trainingId,
  /** Eğitimin tüm sahneleri — `{ id, index, name, narration }`. */
  scenes,
  /**
   * Kaynağa katılan sahne id'leri — sol listedeki anahtarlar.
   *
   * Seçim burada DEĞİL host'ta duruyor: "Çoklu Anlatımdan" ile aynı liste,
   * aynı anahtarlar. Panelin içinde ikinci bir seçim aracı (çip satırı) bir
   * süre denendi ve kaldırıldı — aynı soruyu iki farklı yerde, iki farklı
   * görünümle sormak oluyordu.
   */
  includedIds,
  contentLanguage,
  onServerModal,
  onInsufficientBalance,
  /** Kutu sahneye eklendikten sonra host'u kapatmak için. */
  onInserted,
}) {
  const dispatch = useDispatch();

  const [catalog, setCatalog] = useState(null);
  // İki adım kaldı: TÜM TİPLER açık gelir, seçilen üretilir, sonuç onaylanır.
  //
  // Araya bir "amaç seç → model üç öneri çıkarsın" adımı vardı; kaldırıldı.
  // Gerekçesi şuydu: 23 tipi birden göstermek seçim felci yapar. Her tipin
  // OYNANABİLİR örneği olunca o gerekçe düştü — yazar artık tarif okumuyor,
  // şeye bakıyor. Üstelik öneri adımı bir LLM çağrısıydı: bekleme, maliyet ve
  // "model benim yerime seçti" hissi, artık karşılığı olmayan bir bedel.
  const [step, setStep] = useState("browse"); // browse | result
  // İKİ AYRI ALAN, biri diğerinin yerine geçmez.
  //
  // `narration` AKTİVİTENİN İÇERİĞİ — sahnenin anlatımıyla dolu gelir ve
  // düzenlenebilir. `guidance` ise o içeriğe uygulanacak YÖNLENDİRME: "saha
  // örnekleri kullan", "dili sadeleştir", "şu üç maddeye odaklan".
  //
  // Bir süre tek alan vardı ve yazdığın an anlatımı EZİYORDU. Bu, en çok
  // istenen şeyi imkânsız kılıyordu: sahnenin anlatımından üret ama şuna
  // dikkat et. Yazar bir cümle yazar, sahnenin içeriği sessizce devre dışı
  // kalır ve çıkan aktivitenin sahneyle ilgisi kalmazdı.
  // Anlatım taslakları SAHNE BAŞINA tutuluyor (id → metin), tek bir birleşik
  // metin olarak değil. Birleşik tutulduğunda sahne eklemek/çıkarmak metni
  // yeniden kurmak zorundaydı ve yazarın elle yaptığı düzeltmeler her
  // seferinde gidiyordu. Ayrı taslaklarda bir sahne eklemek yalnızca yeni bir
  // kutu açar; diğerlerine dokunmaz.
  const [draftsById, setDraftsById] = useState({});
  // Hiçbir sahne seçili değilken kullanılan serbest metin — anlatımı olmayan
  // bir sahneye aktivite üretmenin tek yolu bu.
  const [freeText, setFreeText] = useState("");
  const [guidance, setGuidance] = useState("");
  // Aktivitenin ANA rengi. aidea'nın moru seçili gelir; on kareden biri ya da
  // serbest renk seçiciyle değiştirilir.
  const [accentColor, setAccentColor] = useState(DEFAULT_INTERACTIVE_COLOR);
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(null); // 'suggest' | 'generate'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Önizlemesi açık olan tip. Üretimden ÖNCE bakılır, o yüzden hiçbir şeyi
  // tetiklemez ve hiçbir şeye mal olmaz.
  const [example, setExample] = useState(null);

  // Önizleme dokümanı iframe'e src ile VERİLMEZ, çekilip srcdoc olarak
  // verilir — gerekçesi InteractiveItemBox'ta uzun uzun yazılı: varlık geçidi
  // text/html'i attachment olarak servis eder, sandbox'lı çerçeve indiremez,
  // geriye bembeyaz bir kutu kalır. Kanca koşulsuz çağrılır (boş adres
  // zararsız): sonuç ekranı yalnızca bazen çiziliyor, kanca sırası ise her
  // render'da aynı kalmak zorunda.
  const preview = useHtmlByUrl((result && result.htmlUrl) || "");

  useEffect(() => {
    fetchInteractiveCatalog()
      .then(setCatalog)
      .catch((err) => setError(err.message));
  }, []);

  /** Anlatımı olan sahneler — seçilebilir olanlar yalnızca bunlar. */
  const sourceScenes = useMemo(
    () => (scenes || []).filter((s) => s && s.narration && s.narration.trim()),
    [scenes]
  );

  /** Kaynağa katılan sahneler — kurs sırasında. */
  const included = useMemo(
    () => sourceScenes.filter((s) => (includedIds || []).includes(s.id)),
    [sourceScenes, includedIds]
  );

  const setDraft = useCallback(
    (id, value) => setDraftsById((prev) => ({ ...prev, [id]: value })),
    []
  );

  /** Modele gidecek kaynak metin. */
  const narration = useMemo(
    () => (included.length ? composeNarration(included, draftsById) : freeText),
    [included, draftsById, freeText]
  );

  // Kaynak değiştiyse ekrandaki onay kartı artık o kaynağa ait değil: başka
  // sahnelerden üretilmiş bir aktiviteyi onaylatmak sessiz bir hata.
  //
  // Bağımlılık dizinin KENDİSİ değil, birleştirilmiş kimliği: host
  // `includedIds`i her render'da `.filter()` ile üretiyor, yani içerik aynı
  // kalsa da dizi her seferinde yeni. Diziyi doğrudan dinleseydik bu etki her
  // render'da çalışır ve yazar hiçbir zaman bir sonuca bakamazdı.
  const selectionKey = (includedIds || []).join(",");
  useEffect(() => {
    setStep("browse");
    setResult(null);
    setError(null);
    // TASLAKLAR ve YÖNLENDİRME kasıtlı olarak korunuyor: sahne eklemek, başka
    // bir sahnede yapılmış düzeltmeyi ya da "dili sade tut" notunu
    // geçersizleştirmez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  const hasInput = !!(narration.trim() || guidance.trim());
  // Renk artık her zaman dolu (aidea'nın moruyla başlıyor), yani bugün bu
  // `hasInput` ile aynı. Yine de ayrı duruyor: üretim yolunun koşulu "konu VE
  // renk" ve renk bir gün yeniden boşalabilir hâle gelirse, o kuralın tek bir
  // yerde yazılı olması gerekir.
  const canGenerate = hasInput && !!accentColor;

  // Her iki çağrının (öneri ve üretim) paylaştığı gövde.
  const baseFields = useMemo(
    () => ({
      navId,
      sceneTitle,
      sceneIndex,
      trainingId,
      contentLanguage,
      llmModel: model,
      accentColor,
      // İkisi BİRLİKTE gider: içerik anlatımdan, vurgu yönlendirmeden.
      // Backend ikisini zaten ayrı bloklar olarak taşıyor
      // (buildInteractiveUserPrompt), yani burada birleştirmek — ya da birini
      // diğerinin yerine koymak — gereksizdi.
      ...(narration.trim() ? { narrationText: narration.trim() } : {}),
      ...(guidance.trim() ? { prompt: guidance.trim() } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navId,
      sceneTitle,
      sceneIndex,
      trainingId,
      contentLanguage,
      model,
      accentColor,
      narration,
      guidance,
    ]
  );

  const runGenerate = useCallback(
    async (typeId, outline) => {
      setBusy("generate");
      setError(null);
      try {
        const generated = await generateInteractive({ ...baseFields, typeId, outline });
        setResult(generated);
        setStep("result");
      } catch (err) {
        // Bakiye reddini genel bir hata gibi göstermek, kullanıcıya "bir şeyler
        // ters gitti" deyip neyin ters gittiğini saklamak olurdu.
        if (err?.data?.code === "INSUFFICIENT_BALANCE") {
          onInsufficientBalance?.(err.data);
        } else if (err?.data) {
          onServerModal?.(err.data);
        }
        setError(err.message);
      } finally {
        setBusy(null);
      }
    },
    [baseFields, onInsufficientBalance, onServerModal]
  );

  const addToScene = useCallback(() => {
    // Ekleme mantığı ORTAK (applyInteractiveResult): iş tepsisindeki onay da
    // aynı fonksiyonu çağırıyor. İki yerde ayrı yazıldığında biri sessizce
    // geride kalıyor — tepsiden ekleme bir süre hiçbir şey yapmadı.
    const out = applyInteractiveResult(dispatch, result, navId);
    if (!out.ok) {
      // eslint-disable-next-line no-console
      console.warn("[InteractivePane] sahneye eklenemedi:", out.reason, result);
      return;
    }
    setResult(null);
    setStep("browse");
    onInserted?.();
  }, [result, dispatch, navId, onInserted]);

  const types = catalog?.types || [];
  const categories = catalog?.categories || [];

  const examplePopup = example ? (
    <InteractiveExample type={example} onClose={() => setExample(null)} />
  ) : null;

  if (busy === "generate") {
    return (
      <div className="ii-pane">
        {examplePopup}
        <div className="ii-pane__busy">
          <div className="ii-pane__spinner" />
          <div>{i18n.t("InteractiveItem.Generating", { defaultValue: "Aktivite yazılıyor" })}</div>
          <small>
            {i18n.t("InteractiveItem.GeneratingMeta", {
              defaultValue:
                "Birkaç dakika sürebilir — modalı kapatabilirsin, iş arka planda devam eder.",
            })}
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="ii-pane">
      {examplePopup}
      {error && <div className="ii-editor__error">{error}</div>}

      {step === "result" && result ? (
        <>
          <div className="ii-pane__section-head">
            <p className="ii-pane__label">
              {result.typeLabel}
              {result.scored
                ? ` · ${i18n.t("InteractiveItem.Scored", { defaultValue: "puanlı" })}`
                : ` · ${i18n.t("InteractiveItem.Unscored", { defaultValue: "puansız" })}`}
            </p>
          </div>
          {/* Onay ekranı aktiviteyi OYNANABİLİR gösterir: statik bir önizleme,
              "bu doğru çalışıyor mu" sorusunu cevaplamaz. */}
          <iframe
            className="ii-preview"
            srcDoc={preview.directSrc ? undefined : preview.html || ""}
            src={preview.directSrc || undefined}
            title={result.typeLabel}
            sandbox="allow-scripts"
          />
          {preview.failed && (
            <p className="ii-pane__hint">
              {i18n.t("InteractiveItem.PreviewFailed", {
                defaultValue:
                  "Önizleme yüklenemedi — aktivite üretildi, sahneye ekleyip deneyebilirsin.",
              })}
            </p>
          )}
          <div className="ii-pane__foot">
            <button
              type="button"
              className="ii-btn"
              onClick={() => {
                setResult(null);
                setStep("browse");
              }}
            >
              {i18n.t("InteractiveItem.Again", { defaultValue: "Başka bir şey dene" })}
            </button>
            <button type="button" className="ii-btn ii-btn--primary" onClick={addToScene}>
              {i18n.t("InteractiveItem.Add", { defaultValue: "Sahneye ekle" })}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="ii-pane__section-head">
            <p className="ii-pane__label">
              {i18n.t("InteractiveItem.AllTypes", { defaultValue: "Tüm tipler" })}
            </p>
            <select
              className="ii-pane__model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {INTERACTIVE_MODELS.map((m) => (
                <option key={m.value} value={m.value}>
                  {i18n.t(m.labelKey, { defaultValue: m.fallback })}
                </option>
              ))}
            </select>
          </div>

          {/* ANLATIM METİNLERİ — aktivitenin içeriği, SAHNE BAŞINA bir kutu.
              "Çoklu Anlatımdan" sekmesiyle birebir aynı yapı: başlıkta sahne
              adı, yanında geri alma düğmesi, altında düzenlenebilir metin.
              Düğme yalnızca metin orijinalinden FARKLIYKEN görünür — hiçbir
              şeyi geri almayacak bir düğme, yazarı "acaba neyi kaçırdım" diye
              düşündürür.

              Tek birleşik kutu da denendi: sahne eklemek/çıkarmak metni
              yeniden kurmak zorunda kalıyordu ve elle yapılan her düzeltme
              gidiyordu. Ayrı kutularda bir sahne eklemek yalnızca yeni bir
              kutu açar. */}
          {included.length > 0 ? (
            // Yığın KENDİ İÇİNDE kayar ve yüksekliği sınırlıdır.
            //
            // Sınırsız bıraktığımda her seçilen sahne bir kutu daha ekliyordu
            // ve altındaki tip ızgarasını aşağı itiyordu; iki sahnede ızgara
            // ekranın dışına çıktı. Modal 86vh'de kırpıyor ve yukarısında
            // kayan bir kap yok, yani yazar hiçbir aktivite tipini
            // seçemiyordu — seçim özelliği, seçtiği şeyi kullanılamaz
            // hâle getiriyordu.
            <div className="ii-pane__scenes">
              {included.map((s) => {
                const text = draftTextOf(s, draftsById);
                return (
                  <div key={s.id} className="ii-pane__scene">
                    <div className="ii-pane__section-head">
                      <p className="ii-pane__label">{s.name}</p>
                      <button
                        type="button"
                        className="ii-pane__restore"
                        onClick={() => setDraft(s.id, s.narration || "")}
                        style={{
                          visibility: text !== (s.narration || "") ? "visible" : "hidden",
                        }}
                      >
                        {i18n.t("RestoreNarrationText", {
                          defaultValue: "Anlatım metnini getir",
                        })}
                      </button>
                    </div>
                    <textarea
                      className="ii-pane__narration"
                      rows={4}
                      value={text}
                      onChange={(e) => setDraft(s.id, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            // Hiç sahne seçili değil. Serbest metin, anlatımı olmayan bir
            // sahneye aktivite üretmenin tek yolu — kaldırılsaydı o sahneler
            // özelliği hiç kullanamazdı.
            <>
              <div className="ii-pane__section-head">
                <p className="ii-pane__label">
                  {i18n.t("NarrationText", { defaultValue: "Anlatım metni" })}
                </p>
              </div>
              <textarea
                className="ii-pane__narration"
                rows={4}
                value={freeText}
                placeholder={i18n.t("InteractiveItem.NarrationPlaceholder", {
                  defaultValue: "Soldan sahne seç ya da aktivitenin neyi anlatacağını buraya yaz.",
                })}
                onChange={(e) => setFreeText(e.target.value)}
              />
            </>
          )}

          {/* Kaynak soldaki listeden seçiliyor. Burada yalnızca SONUCU
              yazıyoruz — aktivite hangi sahneye düşecek.

              Bunu yazmak şart: seçim çok sahneli olunca hedef artık "baktığın
              sahne" değil, kuralla belirlenen bir sahne (seçilenlerin
              sonuncusu). Yazılmasaydı yazarın tahmin etmesi gerekirdi ve
              aktivite yanlış sahnede belirince bunu ancak kutuyu görünce
              anlardı. */}
          {sceneTitle && (
            <p className="ii-pane__hint">
              {includedIds && includedIds.length > 1
                ? i18n.t("InteractiveItem.TargetMulti", {
                    defaultValue: `${includedIds.length} sahnenin anlatımı kullanılacak · aktivite "${sceneTitle}" sahnesine eklenecek`,
                  })
                : i18n.t("InteractiveItem.TargetSingle", {
                    defaultValue: `Aktivite "${sceneTitle}" sahnesine eklenecek`,
                  })}
            </p>
          )}

          {/* YÖNLENDİRME — içeriği değil, İŞLENİŞİNİ belirler. */}
          <div className="ii-pane__section-head">
            <p className="ii-pane__label">
              {i18n.t("InteractiveItem.Guidance", { defaultValue: "Yönlendirme (opsiyonel)" })}
            </p>
          </div>
          <input
            type="text"
            className="ii-pane__prompt"
            value={guidance}
            placeholder={i18n.t("InteractiveItem.GuidancePlaceholder", {
              defaultValue: "Neye dikkat edilsin? Örn: saha örnekleri ver, dili sade tut",
            })}
            onChange={(e) => setGuidance(e.target.value)}
          />

          {/* ANA RENK — on hazır kare + serbest seçici. Modele tek renk olarak
              değil, paletin türetileceği baskın renk olarak gider. */}
          <div className="ii-color">
            <span className="ii-color__label">
              {i18n.t("InteractiveItem.AccentColor", { defaultValue: "Ana renk" })}
            </span>
            <div className="ii-color__swatches">
              {INTERACTIVE_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  title={c.label}
                  aria-label={c.label}
                  className={
                    accentColor.toUpperCase() === c.value.toUpperCase()
                      ? "ii-swatch ii-swatch--on"
                      : "ii-swatch"
                  }
                  style={{ background: c.value }}
                  onClick={() => setAccentColor(c.value)}
                />
              ))}
              {/* Listede olmayan bir renk isteyen için. Seçici her zaman bir
                  değer taşıdığından, o anki renkle açılır. */}
              <label
                className="ii-swatch ii-swatch--custom"
                title={i18n.t("InteractiveItem.CustomColor", { defaultValue: "Özel renk" })}
              >
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                />
              </label>
            </div>
            <span className="ii-color__note">
              {i18n.t("InteractiveItem.AccentColorNote", {
                defaultValue: "Yüzeyler, tonlar ve degradeler bundan türetilir",
              })}
            </span>
          </div>

          {!canGenerate && (
            <p className="ii-pane__hint">
              {i18n.t("InteractiveItem.NeedInput", {
                defaultValue: "Bu sahnenin anlatımı yok — yukarıya bir konu yaz.",
              })}
            </p>
          )}

          {/* Kredi tahmini — infografik sahne modalındaki düğmenin aynısı,
              aynı yerde (sol alt). Tıklayınca sorar; model değişirse rakam
              atılır, çünkü fiyat modele bağlı. */}
          <div className="ii-pane__credit">
            <InteractiveCreditButton llmModel={model} disabled={!canGenerate} />
          </div>

          {/* TÜM TİPLER açık. Her kartta örneğini açan bir düğme var; yazar
              tarif okumak yerine şeye bakıp seçiyor. */}
          <div className="ii-browse">
            {categories.map((c) => (
              <section key={c.id}>
                <h4>{c.label}</h4>
                <div className="ii-browse__grid">
                  {types
                    .filter((t) => t.category === c.id)
                    .map((t) => (
                      <div className="ii-type-wrap" key={t.id}>
                        <button
                          type="button"
                          className="ii-type"
                          disabled={!canGenerate}
                          onClick={() => runGenerate(t.id, t.sizeHint)}
                        >
                          <strong>{t.label}</strong>
                          <span>{t.blurb}</span>
                          <em>
                            {t.sizeHint}
                            {t.scored
                              ? ` · ${i18n.t("InteractiveItem.Scored", { defaultValue: "puanlı" })}`
                              : ` · ${i18n.t("InteractiveItem.Unscored", { defaultValue: "puansız" })}`}
                          </em>
                        </button>
                        <ExampleButton type={t} onOpen={setExample} floating />
                      </div>
                    ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Bilgi düğmesi — tipin örneğini açar.
 *
 * Yalnızca o tipin örneği YÜKLENMİŞSE çıkar (`type.example`, katalogdan
 * gelir). Örnekler ortam başına ayrı konduğu için bir ortamda eksik olabilir;
 * düğmenin hiç çıkmaması, tıklanınca 404 veren bir düğmeden iyidir.
 */
function ExampleButton({ type, onOpen, floating }) {
  if (!type?.example) return null;
  return (
    <button
      type="button"
      className={floating ? "ii-example-btn ii-example-btn--float" : "ii-example-btn"}
      title={i18n.t("InteractiveItem.SeeExample", { defaultValue: "Örneğini gör" })}
      aria-label={i18n.t("InteractiveItem.SeeExample", { defaultValue: "Örneğini gör" })}
      onClick={(e) => {
        // Kartın kendisi üretimi başlatıyor — bu düğme ona ulaşmamalı.
        e.stopPropagation();
        e.preventDefault();
        onOpen(type);
      }}
    >
      !
    </button>
  );
}
